import { merge, set } from 'lodash-es'

import config from '@/config'

type AuthCallback = (Authorization: string) => void

let refreshingPromise: Promise<any> | boolean | null = false
let refreshSubscribers: AuthCallback[] = []

const onRefreshed = (Authorization: string) => {
  setTimeout(() => {
    refreshSubscribers = refreshSubscribers.filter(cb => cb(Authorization))
    refreshingPromise = false
  }, 200)
}

const subscribeTokenRefresh = (cb: AuthCallback) => {
  refreshSubscribers.push(cb)
}

const createResult = (json: JSON) => ({
  ok: true,
  json: () =>
    new Promise(function (resolve) {
      resolve(json)
    }),
  text: () =>
    new Promise(function (resolve) {
      resolve(JSON.stringify(json))
    }),
})

const customFetch = (uri: string, options: RequestInit): Promise<any> => {
  const initialRequest = fetch(
    uri,
    merge(options, {
      credentials: 'include',
      headers: {
        'Access-Control-Allow-Origin': '*',
        locale: window.__localeId__,
      },
    }),
  )

  return initialRequest
    .then(response => {
      return response.json()
    })
    .then(json => {
      const hasJwtError = ['jwt expired', 'jwt malformed'].includes(
        json?.error ?? '',
      )

      const graphQLError =
        json?.errors?.[0] ??
        json?.[0]?.errors?.[0] ?? // json is an array when batchClient is used
        {}

      const hasAuthError =
        graphQLError.name === 'AuthenticationError' ||
        graphQLError.message === 'jwt expired' ||
        graphQLError.message === 'jwt malformed' ||
        graphQLError?.data?.originalError?.name === 'AuthorizationError' ||
        graphQLError?.data?.originalMessage === 'Permission Denied!' ||
        graphQLError?.data?.originalMessage ===
          'You must be logged in to do this' ||
        graphQLError?.data?.originalMessage ===
          'Response not successful: Received status code 401' ||
        graphQLError?.data?.error === 'jwt expired' ||
        graphQLError?.result?.errors?.[0]?.message === 'jwt expired' ||
        graphQLError?.data?.originalError?.result?.errors?.[0]?.message ===
          'jwt expired' ||
        graphQLError?.data?.originalError?.data?.error === 'jwt expired'

      // force deleted users and users with invalid tokens to be logged out
      if (
        json?.error === 'INACTIVE_USER' ||
        json?.error === 'No user attached to submitted JWT found'
      ) {
        sessionStorage.clear()

        let url = `${config.webRoot}/#/login`

        if (json?.error === 'No user attached to submitted JWT found') {
          url += '?status=expired'
        }

        window.location.href = url
        window.location.reload()
        return
      }

      if (hasJwtError || hasAuthError) {
        subscribeTokenRefresh(auth => {
          let newAuthOptions = { ...options }
          newAuthOptions = set(newAuthOptions, 'headers.authorization', auth)

          const repeatedRequest = fetch(
            uri,
            merge(newAuthOptions, {
              credentials: 'include',
              headers: {
                'Access-Control-Allow-Origin': '*',
              },
            }),
          ).catch(e => console.log(e, 'eee'))
          return repeatedRequest
        })
        if (!refreshingPromise) {
          const refresh_token = sessionStorage.getItem('refreshToken')
          const address = `${config.endpoints.auth}/token/refresh`

          const formData = new URLSearchParams()
          formData.append('token', refresh_token ?? '')

          // Execute the re-authorization request and set the promise returned to refreshingPromise
          refreshingPromise = fetch(address, {
            method: 'POST',
            body: formData,
            credentials: 'include',
            headers: {
              'Access-Control-Allow-Origin': '*',
              authorization: refresh_token ? `Bearer ${refresh_token}` : '',
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }).then(refreshTokenResponse => {
            if (refreshTokenResponse.ok) {
              return refreshTokenResponse
                .json()
                .then(({ accessToken, refreshToken, userScope }) => {
                  sessionStorage.setItem('accessToken', accessToken)
                  sessionStorage.setItem('refreshToken', refreshToken)
                  sessionStorage.setItem('userScope', userScope)

                  return accessToken
                })
            } else {
              refreshTokenResponse.json().then(json => {
                console.error(`Refresh failed: ${json?.error}`)
              })
              sessionStorage.clear()

              //  window.location.href = `${config.webRoot}/login`
            }
          })
        }

        if (typeof refreshingPromise === 'boolean') {
          return
        }

        return refreshingPromise
          .then(async (newAccessToken: string) => {
            refreshingPromise = null

            const Authorization = `Bearer ${newAccessToken}`

            options = set(options ?? {}, 'headers.authorization', Authorization)

            const response = await fetch(
              uri,
              merge(options, {
                credentials: 'include',
                headers: {
                  'Access-Control-Allow-Origin': '*',
                },
              }),
            )
            const newJSON = await response.json()
            await onRefreshed(Authorization)
            return createResult(newJSON)
          })
          .catch((e: any) => e)
      }

      return createResult(json)
    })
    .catch(() => {})
}

export default customFetch
